import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Badge,
  Text,
  Heading,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  VStack,
  Divider
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';

const VarianceIcd10Card = ({ report }) => {
  if (!report) return null;

  const getIcd10Lists = (variance) => {
    if (!variance) return { matches: [], missing: [], extra: [] };
    return {
      matches: variance.exact_matches || [],
      missing: variance.missing_in_prospect || [],
      extra: variance.extra_in_prospect || []
    };
  };

  const icd = getIcd10Lists(report.icd10_variance);
  const hasVariance = icd.missing.length > 0 || icd.extra.length > 0;
  const aiEval = report.icd10_ai_evaluation;

  const renderCodeBadges = (codes, colorScheme, prefix = "") => {
    if (!codes || codes.length === 0) return null;
    return (
      <Flex flexWrap="wrap" gap={1}>
        {codes.map((code, idx) => (
          <Badge key={idx} colorScheme={colorScheme} variant="subtle" title={prefix}>
            {code}
          </Badge>
        ))}
      </Flex>
    );
  };

  const markdownStyles = {
    'p': { marginBottom: '0.5rem', whiteSpace: 'pre-wrap' },
    'ul': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'ol': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'li': { marginBottom: '0.25rem' },
    'strong': { fontWeight: 'bold' },
    'em': { fontStyle: 'italic' }
  };

  return (
    <Card mb={6} shadow="sm" borderWidth="1px" borderColor={hasVariance ? "orange.200" : "green.200"}>
      <CardHeader bg={hasVariance ? "orange.50" : "green.50"} py={3} borderBottomWidth="1px">
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Badge colorScheme={hasVariance ? "orange" : "green"} fontSize="sm">
              {hasVariance ? "ICD-10 Variance Detected" : "ICD-10 Perfect Match"}
            </Badge>
            <Text fontWeight="bold" color="gray.800">ICD-10 Analysis</Text>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
          {/* Left Side: Deterministic Data */}
          <GridItem>
            <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Deterministic Variance</Heading>
            
            <Grid templateColumns={report.legacy_mn_icd10_data && report.legacy_mn_icd10_data.length > 0 ? "1fr 1fr 1fr" : "1fr 1fr"} gap={4} mb={4}>
              <Card variant="outline" borderColor="gray.200" shadow="none">
                <CardHeader bg="gray.50" py={2} px={3} borderBottomWidth="1px">
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">Claim Billed</Text>
                </CardHeader>
                <CardBody p={2}>
                  <VStack align="stretch" spacing={2}>
                    {(report.prospect_icd10_data || []).map((item, idx) => {
                      const code = item.code || item;
                      const normalizedCode = code.replace('.', '').toUpperCase();
                      const isMatch = icd.matches.includes(normalizedCode);
                      const color = isMatch ? "green" : "yellow";
                      
                      // Check if explicitly in text
                      const inDoc = report.icd10_variance?.codes_in_text?.includes(normalizedCode);

                      return (
                        <Flex key={idx} justify="space-between" align="center" p={1.5} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                          <Text fontSize="xs" fontWeight="bold" color={`${color}.700`}>Order {idx + 1}</Text>
                          <HStack>
                            {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                            <Badge colorScheme={color} fontSize="sm">{code}</Badge>
                          </HStack>
                        </Flex>
                      );
                    })}
                    {(!report.prospect_icd10_data || report.prospect_icd10_data.length === 0) && (
                      <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No codes billed</Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              <Card variant="outline" borderColor="gray.200" shadow="none">
                <CardHeader bg="blue.50" py={2} px={3} borderBottomWidth="1px" borderColor="blue.100">
                  <Text fontSize="xs" fontWeight="bold" color="blue.800">Revedy Recommended</Text>
                </CardHeader>
                <CardBody p={2}>
                  <VStack align="stretch" spacing={2}>
                    {(report.revedy_icd10_data || []).map((code, idx) => {
                      const normalizedCode = code.replace('.', '').toUpperCase();
                      const isMatch = icd.matches.includes(normalizedCode);
                      const color = isMatch ? "green" : "blue";
                      
                      // Check if explicitly in text
                      const inDoc = report.icd10_variance?.codes_in_text?.includes(normalizedCode);

                      return (
                        <Flex key={idx} justify="space-between" align="center" p={1.5} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                          <Text fontSize="xs" fontWeight="bold" color={`${color}.700`}>Order {idx + 1}</Text>
                          <HStack>
                            {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                            <Badge colorScheme={color} fontSize="sm">{code}</Badge>
                          </HStack>
                        </Flex>
                      );
                    })}
                    {(!report.revedy_icd10_data || report.revedy_icd10_data.length === 0) && (
                      <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No codes recommended</Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Legacy MN Data Display */}
              {report.legacy_mn_icd10_data && report.legacy_mn_icd10_data.length > 0 && (
                <Card variant="outline" borderColor="gray.200" shadow="none">
                  <CardHeader bg="gray.50" py={2} px={3} borderBottomWidth="1px">
                    <Text fontSize="xs" fontWeight="bold" color="gray.600">Legacy Analysis</Text>
                  </CardHeader>
                  <CardBody p={2}>
                    <VStack align="stretch" spacing={2}>
                      {report.legacy_mn_icd10_data.map((code, idx) => (
                        <Flex key={idx} justify="space-between" align="center" p={1.5} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                          <Text fontSize="xs" fontWeight="bold" color="gray.500">Order {idx + 1}</Text>
                          <Badge colorScheme="gray" variant="outline" fontSize="sm">{code}</Badge>
                        </Flex>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </Grid>

            <VStack align="stretch" spacing={3} mt={4} pt={4} borderTopWidth="1px" borderColor="gray.100">
              {icd.matches.length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="green.600" mb={1}>Exact Matches</Text>
                  {renderCodeBadges(icd.matches, "green")}
                </Box>
              )}
              {icd.missing.length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="blue.600" mb={1}>Revedy Only (Missing in Claim)</Text>
                  {renderCodeBadges(icd.missing, "blue")}
                </Box>
              )}
              {icd.extra.length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="yellow.600" mb={1}>Claim Only (Extra in Claim)</Text>
                  {renderCodeBadges(icd.extra, "yellow")}
                </Box>
              )}
              {!hasVariance && icd.matches.length === 0 && (
                <Text fontSize="sm" color="gray.400">No ICD-10 data available.</Text>
              )}
            </VStack>
            
            {/* ICD-10 Optimization Section */}
            {report.icd10_optimization && (report.icd10_optimization.prospect || report.icd10_optimization.revedy) && (
              <Box mt={6} pt={4} borderTopWidth="1px" borderColor="gray.200">
                <Heading size="xs" color="teal.600" textTransform="uppercase" mb={3}>Payer Rules Optimization</Heading>
                <Grid templateColumns="1fr 1fr" gap={4}>
                  {report.icd10_optimization.prospect && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Claim Billed Optimization</Text>
                      
                      {/* Critical Warnings */}
                      {report.icd10_optimization.prospect.warnings?.critical?.length > 0 && (
                        <VStack align="stretch" spacing={1} mb={2}>
                          {report.icd10_optimization.prospect.warnings.critical.map((w, i) => (
                            <Text key={i} fontSize="xs" color="red.600" fontWeight="bold">
                              ⚠️ CRITICAL: {w}
                            </Text>
                          ))}
                        </VStack>
                      )}
                      
                      {/* Notes */}
                      {report.icd10_optimization.prospect.warnings?.notes?.length > 0 && (
                        <VStack align="stretch" spacing={1} mb={3}>
                          {report.icd10_optimization.prospect.warnings.notes.map((w, i) => (
                            <Text key={i} fontSize="xs" color="gray.500" fontStyle="italic">
                              ℹ️ {w}
                            </Text>
                          ))}
                        </VStack>
                      )}
                      
                      {(!report.icd10_optimization.prospect.warnings?.critical?.length && !report.icd10_optimization.prospect.warnings?.notes?.length) && (
                        <Text fontSize="xs" color="green.600" mb={3}>✓ No payer rule violations</Text>
                      )}
                      
                      <Text fontSize="xs" color="gray.500" mb={1}>Optimal Order:</Text>
                      <VStack align="stretch" spacing={2}>
                        {(report.icd10_optimization.prospect.optimal_codes || []).map((item, idx) => (
                          <Box key={idx} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                            <HStack mb={1}>
                              <Badge colorScheme="teal">{item.code || item}</Badge>
                            </HStack>
                            {item.reason && <Text fontSize="2xs" color="teal.700">{item.reason}</Text>}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                  
                  {report.icd10_optimization.revedy && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Revedy Optimization</Text>
                      
                      {/* Critical Warnings */}
                      {report.icd10_optimization.revedy.warnings?.critical?.length > 0 && (
                        <VStack align="stretch" spacing={1} mb={2}>
                          {report.icd10_optimization.revedy.warnings.critical.map((w, i) => (
                            <Text key={i} fontSize="xs" color="red.600" fontWeight="bold">
                              ⚠️ CRITICAL: {w}
                            </Text>
                          ))}
                        </VStack>
                      )}
                      
                      {/* Notes */}
                      {report.icd10_optimization.revedy.warnings?.notes?.length > 0 && (
                        <VStack align="stretch" spacing={1} mb={3}>
                          {report.icd10_optimization.revedy.warnings.notes.map((w, i) => (
                            <Text key={i} fontSize="xs" color="gray.500" fontStyle="italic">
                              ℹ️ {w}
                            </Text>
                          ))}
                        </VStack>
                      )}
                      
                      {(!report.icd10_optimization.revedy.warnings?.critical?.length && !report.icd10_optimization.revedy.warnings?.notes?.length) && (
                        <Text fontSize="xs" color="green.600" mb={3}>✓ No payer rule violations</Text>
                      )}
                      
                      <Text fontSize="xs" color="gray.500" mb={1}>Optimal Order:</Text>
                      <VStack align="stretch" spacing={2}>
                        {(report.icd10_optimization.revedy.optimal_codes || []).map((item, idx) => (
                          <Box key={idx} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                            <HStack mb={1}>
                              <Badge colorScheme="teal">{item.code || item}</Badge>
                            </HStack>
                            {item.reason && <Text fontSize="2xs" color="teal.700">{item.reason}</Text>}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Grid>
              </Box>
            )}
          </GridItem>

          {/* Right Side: AI Evaluation */}
          <GridItem borderLeftWidth={{ base: "0", lg: "1px" }} pl={{ base: 0, lg: 8 }} pt={{ base: 4, lg: 0 }}>
            <Heading size="xs" color="purple.600" textTransform="uppercase" mb={3}>AI Analysis</Heading>
            {aiEval && (aiEval.discrepancy_summary || aiEval.overall_winner) ? (
              <VStack align="stretch" spacing={4}>
                
                {/* Winner & Scores Section */}
                <Box p={3} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.700" textTransform="uppercase">Evaluation Result</Text>
                    <Badge 
                      colorScheme={
                        aiEval.overall_winner?.toLowerCase() === 'revedy' ? 'blue' : 
                        aiEval.overall_winner?.toLowerCase() === 'claim' ? 'orange' : 
                        aiEval.overall_winner?.toLowerCase() === 'tie' ? 'green' : 'gray'
                      }
                      fontSize="sm"
                      px={2}
                      py={1}
                    >
                      WINNER: {aiEval.overall_winner ? aiEval.overall_winner.toUpperCase() : 'N/A'}
                    </Badge>
                  </Flex>
                  
                  <Grid templateColumns="1fr 1fr" gap={4} mb={3}>
                    <Box p={2} bg="white" rounded="md" borderWidth="1px" textAlign="center">
                      <Text fontSize="2xs" color="gray.500" fontWeight="bold">REVEDY SCORE</Text>
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                        {aiEval.revedy_score !== undefined && aiEval.revedy_score !== null ? `${aiEval.revedy_score}/10` : 'N/A'}
                      </Text>
                    </Box>
                    <Box p={2} bg="white" rounded="md" borderWidth="1px" textAlign="center">
                      <Text fontSize="2xs" color="gray.500" fontWeight="bold">CLAIM SCORE</Text>
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">
                        {aiEval.claim_score !== undefined && aiEval.claim_score !== null ? `${aiEval.claim_score}/10` : 'N/A'}
                      </Text>
                    </Box>
                  </Grid>

                  <Box>
                    <Text fontSize="2xs" fontWeight="bold" color="gray.500" mb={1}>OVERALL REASONING</Text>
                    <Text fontSize="xs" color="gray.700">
                      {aiEval.overall_reasoning || 'N/A'}
                    </Text>
                  </Box>
                </Box>

                {/* Improvement Suggestion (Only show if actionable) */}
                {aiEval.revedy_improvement_suggestion && 
                 aiEval.revedy_improvement_suggestion.toLowerCase() !== 'none' && 
                 aiEval.revedy_improvement_suggestion.toLowerCase() !== 'n/a' && (
                  <Box p={3} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.200">
                    <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={1} textTransform="uppercase">Improvement Suggestion</Text>
                    <Text fontSize="xs" color="orange.900">{aiEval.revedy_improvement_suggestion}</Text>
                  </Box>
                )}

                <Divider borderColor="gray.200" />

                {/* Narrative Summaries */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>Discrepancy Summary</Text>
                  <Text fontSize="sm">{aiEval.discrepancy_summary || 'N/A'}</Text>
                </Box>

                {aiEval.revedy_justification && (
                  <Box p={3} bg="blue.50" rounded="md" borderWidth="1px" borderColor="blue.100">
                    <Text fontSize="xs" fontWeight="bold" color="blue.800" mb={2}>Revedy Justification</Text>
                    <Box fontSize="xs" color="blue.900" sx={markdownStyles}>
                      <ReactMarkdown>{aiEval.revedy_justification}</ReactMarkdown>
                    </Box>
                  </Box>
                )}

                {aiEval.claim_rationale_hypothesis && (
                  <Box p={3} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.100">
                    <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={2}>Claim Rationale Hypothesis</Text>
                    <Box fontSize="xs" color="orange.900" sx={markdownStyles}>
                      <ReactMarkdown>{aiEval.claim_rationale_hypothesis}</ReactMarkdown>
                    </Box>
                  </Box>
                )}

              </VStack>
            ) : hasVariance ? (
              <Text fontSize="sm" color="gray.500" fontStyle="italic">AI analysis not generated.</Text>
            ) : (
              <Text fontSize="sm" color="green.600">No differences to analyze.</Text>
            )}
          </GridItem>
        </Grid>
      </CardBody>
    </Card>
  );
};

export default VarianceIcd10Card;